import React from "react";
import * as THREE from "three";
import * as global from '../../../data/global';
import { buildSticeWallGeometriesFromLayoutSides } from './sticeWallGeometry';
import {
    buildWallGeometriesFromPolygon,
    centerPolygonFtAtOrigin,
    rectPolyFtFromBinSize,
} from './layoutPreviewShape';
import { previewPolygonFtForSite } from '../../../components/layoutPreviewView';
import { buildBinLocalPolygonFromLayoutSides } from '../../../components/layoutTableProxy';
import { catalogFootprintDimensionsFromLayoutBin } from '../../../components/siteLayoutStice.js';

/**
 * Flat stice walls: extruded shape from drawSite lx/hlx segments (production grainwaveclient path).
 */
export default function SticeArcWall(props) {
    if (global.gw.selectedSite?.isLayoutPreview) {
        return <group name="SticeArcWall" />;
    }

    const floorMaterial = new THREE.MeshLambertMaterial({
        color: 0xCCCCCC,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        depthTest: false,
    });
    const skipInvisibleFloor =
        props.site?.isLayoutPreviewSite || props.site?.isLayoutPreview3d;
    const height = props.height ?? 1.25;
    const radialSegments = props.radialSegments || 12;
    const extrudeSettings = {
        depth: height,
        curveSegments: radialSegments,
        bevelEnabled: false,
        bevelSegments: 1,
        steps: 1,
        bevelSize: 0,
        bevelThickness: 1,
    };
    const sliceColor = global.gw.theme === 'light' ? 0x000000 : 0xCCCCCC;
    const isBinWindowView = !!global.gw.selectedSite?.isBinWindowView;
    const sliceOpacity =
        props.site?.isLayoutPreviewSite || props.site?.isLayoutPreview3d
            ? global.gw.theme === 'light'
                ? 0.35
                : 0.12
            : isBinWindowView
              ? global.gw.theme === 'light'
                  ? 0.35
                  : 0.2
              : global.gw.theme === 'light'
                ? 0.2
                : 0.045;
    const layoutEditorPreview = !!(
        props.site?.isLayoutPreviewSite ||
        props.site?.isLayoutPreview3d ||
        props.site?.layoutUsesEditorLayoutAxes
    );
    const material = new THREE.MeshPhongMaterial({
        color: sliceColor,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: sliceOpacity,
        depthWrite: layoutEditorPreview,
        toneMapped: true,
    });

    let geometry = null;
    let floorGeometry = null;

    const layoutBin =
        props.layoutBin ??
        props.site?.layoutBins?.find(
            (lb) => lb.customerSiteLayoutBinId === props.binId
        );

    // Round silos: cylinder only. Layout sides on Temputech round fields are neighbor
    // arcs / channels — chaining them produces empty or axis-shaped walls in /bin.
    const drawRoundCylinder = !!(
        (layoutBin?.isRound || props.radiusTop > 0) &&
        !(props.layoutSides?.length >= 3 && !layoutBin?.isRound)
    );

    let previewPoly = null;
    if (!drawRoundCylinder) {
        // Editor layout / Preview: the face polygon is the source of truth (matches 2D).
        // Rebuilding from layoutSides can re-chain walls+arcs into a skewed footprint.
        if (
            layoutEditorPreview &&
            layoutBin?.previewPolygonFt?.length >= 3
        ) {
            previewPoly = previewPolygonFtForSite(
                props.site,
                layoutBin.previewPolygonFt
            );
        }
        // Production / legacy: prefer live layoutSides so cables and walls share one footprint.
        if (!(previewPoly?.length >= 3) && layoutBin && props.layoutSides?.length >= 3) {
            previewPoly = buildBinLocalPolygonFromLayoutSides(
                layoutBin,
                props.layoutSides
            );
        }
        if (!(previewPoly?.length >= 3)) {
            previewPoly = previewPolygonFtForSite(
                props.site,
                layoutBin?.previewPolygonFt
            );
        }
        if (isBinWindowView && previewPoly?.length >= 3) {
            previewPoly = centerPolygonFtAtOrigin(previewPoly);
        }
        if (previewPoly?.length >= 3) {
            ({ geometry, floorGeometry } = buildWallGeometriesFromPolygon(
                previewPoly,
                height,
                radialSegments
            ));
        } else if (props.layoutSides && props.layoutSides.length > 0) {
            ({ geometry, floorGeometry } = buildSticeWallGeometriesFromLayoutSides(
                props.layoutSides,
                extrudeSettings
            ));
        }
    }

    if (!geometry && layoutBin && !layoutBin.isRound && !props.radiusTop) {
        const { width: w, length: len } = catalogFootprintDimensionsFromLayoutBin(
            layoutBin,
            props.site
        );
        const rectPoly = rectPolyFtFromBinSize(w, len);
        ({ geometry, floorGeometry } = buildWallGeometriesFromPolygon(
            isBinWindowView ? centerPolygonFtAtOrigin(rectPoly) : rectPoly,
            height,
            radialSegments
        ));
    }

    if (!geometry && props.radiusTop > 0) {
        const shape = new THREE.Shape();
        const hole = new THREE.Shape();
        shape.moveTo(0, 0).absarc(0, 0, props.radiusTop, 0, Math.PI * 2, true);
        hole.moveTo(0, 0).absarc(0, 0, props.radiusTop - 0.5, 0, Math.PI * 2, true);
        shape.holes.push(hole);
        try {
            geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
            geometry.rotateX(Math.PI / 2);
            const floor = new THREE.Shape();
            floor.copy(hole);
            floorGeometry = new THREE.ExtrudeGeometry(floor, extrudeSettings);
            floorGeometry.rotateX(-Math.PI / 2);
        } catch {
            geometry = null;
            floorGeometry = null;
        }
    }

    return (
        <group name="SticeArcWall">
            {geometry ?
                <group>
                    <mesh
                        binId={props.binId}
                        binName={props.binName}
                        binDataId={props.binDataId}
                        isBinMesh={true}
                        isWallMesh={true}
                        geometry={geometry}
                        material={material}
                        originalMaterial={material.clone()}
                    />
                    {floorGeometry && !skipInvisibleFloor ? (
                        <mesh
                            binId={props.binId}
                            binName={props.binName}
                            binDataId={props.binDataId}
                            isBinMesh={true}
                            isWallMesh={false}
                            geometry={floorGeometry}
                            material={floorMaterial}
                            originalMaterial={floorMaterial.clone()}
                        />
                    ) : null}
                </group>
                : null}
        </group>
    );
}
